import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTheme } from '../../hooks/useTheme';
import profilBild from '../../assets/Systemische Beratung in Dresden - Madlin Firme.jpg';
import logo from '../../assets/Systemische Beratung in Dresden - Madlin Firme.png';

const IMAGES = {
  profile: {
    width: 380,
    aspect: 4 / 5,
    radius: 'var(--radius-xl)',
  },
};

const AboutSection = () => {
  const { features } = useTheme();
  const reduceMotion = useReducedMotion();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });

  const timeline = [
    { year: '2026', title: 'Selbstständigkeit', description: 'Nebenberufliche Selbstständigkeit als systemische Beraterin' },
    { year: '2025', title: 'Weiterbildung Systemische Beratung', description: 'Sofi, Dresden' },
    { year: '2022', title: 'Sozialarbeiterin im Bereich der ambulanten Hilfen zur Erziehung', description: 'NE-Natürliches Erleben gGmbH, Dresden' },
    { year: '2021', title: 'Straßenjugendsozialarbeiterin', description: 'Johanniter-Unfall-Hilfe e.V., Nordhausen' },
    { year: '2021', title: 'Bachelor of Arts', description: 'Gesundheits- und Sozialwesen, Hochschule Nordhausen' },
  
    
    
  ];

  const qualifications = [
    'Staatlich anerkannte Sozialarbeiterin / Sozialpädagogin (B.A.)',
    'Zertifizierte systemische Beraterin (SG – Systemische Gesellschaft)',
  ];

  return (
    <section id="about" className="py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: reduceMotion ? 0 : 0.55, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Über mich
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* LEFT */}
<div className="space-y-8 flex flex-col justify-center min-h-full">
  {/* Überschrift & Untertitel */}
  <div className="text-center lg:text-left"> {/* Text auch zentrieren? Optional */}
    <h3 className="font-serif text-2xl md:text-3xl" style={{ color: 'var(--color-text-primary)' }}>
      Madlin Firme
    </h3>
    <p style={{ color: 'var(--color-text-muted)' }} className="mt-1 text-sm md:text-base">
      Systemische Beratung &amp; Begleitung
    </p>
  </div>

  {/* Bild-Container */}
  <div className="flex justify-left">
    <div style={{ width: '100%', maxWidth: IMAGES.profile.width }}>
      <img
        src={profilBild}
        alt="Profilbild"
        className={features?.organicShapes ? 'organic-shape' : ''}
        style={{
          width: '100%',
          maxWidth: '100%',
          aspectRatio: `${IMAGES.profile.aspect}`,
          objectFit: 'cover',
          borderRadius: IMAGES.profile.radius,
          boxShadow: 'var(--shadow-md)',
          border: '1px solid var(--color-border-subtle)',
          backgroundColor: 'var(--color-surface-1)',
          display: 'block',
        }}
      />
    </div>
  </div>

              <div className="space-y-4">
                <p style={{ color: 'var(--color-text-secondary)' }} className="leading-relaxed">
                  Als systemische Beraterin möchte ich verstehen, was Sie wirklich bewegt und unterstütze Sie dabei,
                  Ihre eigenen Lösungen zu entwickeln. Gemeinsam erkunden wir neue Perspektiven und finden Wege, die
                  für Sie passend sind.
                </p>
                <p style={{ color: 'var(--color-text-secondary)' }} className="leading-relaxed">
                  Meine Arbeitsweise basiert auf einer wertschätzenden, akzeptierenden und neugierigen Grundhaltung
                  sowie auf der Annahme, dass Sie bereits alles in sich tragen, was Sie für Veränderungen benötigen.
                </p>
              </div>
            </div>

            {/* RIGHT */}
            <div className="space-y-8">


{/* Werdegang Bereich */}
<div className="py-1.5"> {/* Mehr Atempause nach oben/unten */}
  <h3 className="font-serif text-3xl mb-12 tracking-tight" style={{ color: 'var(--color-text-primary)' }}>
    Mein Werdegang
  </h3>

  <div className="relative overflow-x-hidden" style={{ paddingLeft: '0' }}>
    {/* Die vertikale Linie */}
    <div
      className="absolute top-2 bottom-0 w-[2px] rounded-full"
      style={{ 
        left: '52px',
        background: 'linear-gradient(to bottom, var(--color-accent-500) 0%, var(--color-secondary-300) 100%)',
        opacity: 0.3 
      }}
    />

    <div className="space-y-8">
      {timeline.map((t, index) => (
        <div key={index} className="relative flex group" style={{ gap: '0' }}>
          {/* Jahr als prominenter Marker – feste Breite */}
          <div className="flex-shrink-0 pt-1" style={{ width: '52px', minWidth: '52px' }}>
            <span className="font-serif text-base font-medium block leading-none" style={{ color: 'var(--color-text-primary)' }}>
              {t.year}
            </span>
          </div>

          {/* Punkt auf der Linie – exakt auf der Linie bei left=52px */}
          <div
            className="absolute top-[6px] w-4 h-4 rounded-full border-2 border-white shadow-sm transition-transform duration-300 group-hover:scale-150 flex-shrink-0"
            style={{ 
              left: '44px',
              backgroundColor: 'var(--color-accent-500)', 
              zIndex: 1 
            }}
          />

          {/* Text-Inhalt – Abstand vom Punkt */}
          <div className="flex-grow" style={{ paddingLeft: '28px' }}>
            <h4 className="text-base sm:text-xl font-bold mb-2 tracking-tight" style={{ color: 'var(--color-text-primary)' }}>
              {t.title}
            </h4>
            <p className="text-sm sm:text-base leading-relaxed" style={{ color: 'var(--color-text-secondary)', opacity: 0.8 }}>
              {t.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>

              {/* Qualifikationen */}
              <div className="surface p-4" style={{ borderRadius: 'var(--radius-xl)', marginTop: '60px' }}>
                <h4 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                  Qualifikationen
                </h4>
                <ul className="space-y-2">
                  {qualifications.map((q) => (
                    <li key={q} className="flex items-start gap-3" style={{ color: 'var(--color-text-secondary)' }}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 10,
                          height: 10,
                          marginTop: 9,
                          borderRadius: 999,
                          backgroundColor: 'var(--color-primary-500)',
                          flex: '0 0 auto',
                        }}
                      />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-2 mt-4">
              <div 
                className="surface p-8 md:p-12 flex flex-col md:flex-row items-center gap-10" 
                style={{ 
                  borderRadius: 'var(--radius-xl)',
                  border: '1px solid var(--color-border-subtle)',
                  boxShadow: 'var(--shadow-md)'
                }}
              >
                <div className="flex-shrink-0 -mt-2">
                    <img
                      src={logo}
                      alt="Logo"
                      style={{
                        width: '140px',
                        height: 'auto',
                        objectFit: 'contain',
                        opacity: 0.9
                      }}
                    />
                </div>

                {/* Philosophie Text Teil */}
                <div className="flex-1 text-center md:text-left">
                  <h4 className="font-serif text-2xl mb-3" style={{ color: 'var(--color-text-primary)' }}>
                    Meine Philosophie
                  </h4>
                  <p style={{ color: 'var(--color-text-secondary)' }} className="italic text-lg leading-relaxed">
                    „Wie bei einem Mobile beeinflusst sich alles gegenseitig und schon kleine Bewegungen können spürbare
                    Veränderungen bewirken. Meine Aufgabe ist es, Sie dabei zu unterstützen, diese Dynamiken zu erkennen,
                    um neue Perspektiven zu gewinnen und Veränderung bewusst zu gestalten.“
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;